// src/pages/AnalysisHistoryPage.jsx
// Analysis History — document hub organized by brand (folder metaphor)

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AppShell from "../components/AppShell";
import UserActionsBar from "../components/UserActionsBar";
import ConfirmDialog from "../components/ConfirmDialog";
import {
  fetchBrands,
  copilotListSessions,
  analysisRunsList,
  analysisRunsActiveByBrand,
  analysisRunCostBreakdown,
  analysisRunTransitionStatus,
  analysisRunAbort,
  analysisRunSoftDelete,
} from "../services/api";


// Wizard stages mapped to run statuses (mirrors NewAnalysisPage STAGES).
// Stage 2 owns running_audit so the overview reads "Audit" while models
// are being queried — matching the wizard + AnalysisRunDetailPage header.
const WIZARD_STAGES = [
  { key: 1, label: "Configure", statuses: ["pending"] },
  { key: 2, label: "Audit", statuses: ["running_audit", "audit_complete"] },
  { key: 3, label: "Analysis", statuses: ["running_analysis", "analysis_complete"] },
  { key: 4, label: "Deliver", statuses: ["generating_pptx", "ready", "delivered"] },
];

function stageForStatus(status) {
  for (const s of WIZARD_STAGES) if (s.statuses.includes(status)) return s.key;
  return 1;
}

// Coarse filter groups (what the hub chips offer)
const STATUS_GROUPS = {
  in_progress: ["running_audit", "running_analysis", "generating_pptx"],
  needs_review: ["audit_complete", "analysis_complete"],
  ready: ["ready"],
  delivered: ["delivered"],
  failed: ["failed"],
  pending: ["pending"],
};

function statusColor(status) {
  if (status === "delivered" || status === "ready") return { bg: "#d1fae5", fg: "#065f46" };
  if (status === "failed") return { bg: "#fee2e2", fg: "#991b1b" };
  if (["running_audit", "running_analysis", "generating_pptx"].includes(status))
    return { bg: "#fef3c7", fg: "#92400e" };
  if (["audit_complete", "analysis_complete"].includes(status))
    return { bg: "#dbeafe", fg: "#1e40af" };
  return { bg: "#f3f4f6", fg: "#374151" };
}

// ---------------------------------------------------------------------------
// Theme constants
// ---------------------------------------------------------------------------
const GOLD = "#FFCC33";
const NAVY = "#1a1f36";
const SIDEBAR_W = 280;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDate(iso) {
  if (!iso) return "--";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function modelLabel(model) {
  if (!model) return "default";
  // Trim common prefixes for readability
  return model.replace(/^models\//, "");
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SearchBar({ value, onChange, placeholder }) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || "Search sessions..."}
      style={{
        width: "100%",
        padding: "10px 14px",
        borderRadius: 8,
        border: "1px solid #ddd",
        fontSize: 14,
        outline: "none",
        boxSizing: "border-box",
      }}
    />
  );
}

// LLM183: inline "$X.YZ" link that opens a popover with per-stage breakdown
// (audit / analysis / pptx). Lazy-fetches on first open and caches.
function CostBreakdown({ runId, totalUsd }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const wrapRef = React.useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const onToggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (open) { setOpen(false); return; }
    if (!data && !loading) {
      setLoading(true);
      try {
        const res = await analysisRunCostBreakdown(runId);
        setData(res);
      } catch {
        // Silent — show whatever we have
      } finally {
        setLoading(false);
      }
    }
    setOpen(true);
  };

  return (
    <span ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          color: "#1e3a8a",
          fontSize: 11,
          fontWeight: 700,
          cursor: "pointer",
          textDecoration: "underline dotted",
        }}
        title="Click for cost breakdown"
      >
        ${totalUsd.toFixed(3)}
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(15,23,42,0.18)",
            padding: 10,
            minWidth: 200,
            zIndex: 100,
            color: "#111827",
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
            Cost breakdown
          </div>
          {loading || !data ? (
            <div style={{ fontSize: 12, color: "#9ca3af" }}>Loading…</div>
          ) : (
            <div style={{ fontSize: 12, display: "flex", flexDirection: "column", gap: 4 }}>
              <Row label="Audit" value={data.audit_cost_usd} />
              <Row label="Analysis" value={data.analysis_cost_usd} />
              <Row label="PPTX" value={data.pptx_cost_usd} />
              <div style={{ height: 1, background: "#e5e7eb", margin: "4px 0" }} />
              <Row label="Total" value={data.total_cost_usd} bold />
            </div>
          )}
        </div>
      )}
    </span>
  );
}

function Row({ label, value, bold }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <span style={{ color: bold ? "#111827" : "#6b7280", fontWeight: bold ? 800 : 600 }}>{label}</span>
      <span style={{ color: "#111827", fontWeight: bold ? 900 : 700, fontVariantNumeric: "tabular-nums" }}>
        ${Number(value || 0).toFixed(3)}
      </span>
    </div>
  );
}


function BrandFolder({ brand, count, activeCount = 0, activeRuns = [], selected, onClick }) {
  const isAll = brand === null;
  const tooltip =
    activeRuns.length > 0
      ? `${activeCount} active run${activeCount === 1 ? "" : "s"}\n` +
        activeRuns
          .map((r) => `• ${r.title || `Run #${r.id}`}${r.assignee_name ? ` — ${r.assignee_name}` : ""}`)
          .join("\n")
      : undefined;
  return (
    <button
      onClick={onClick}
      title={tooltip}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        padding: "10px 14px",
        borderRadius: 8,
        border: "none",
        background: selected
          ? `linear-gradient(135deg, ${NAVY}, #2a2f4a)`
          : "transparent",
        color: selected ? "#fff" : NAVY,
        cursor: "pointer",
        fontSize: 14,
        fontWeight: selected ? 700 : 500,
        textAlign: "left",
        transition: "background 0.15s",
      }}
    >
      {/* Folder icon */}
      <span style={{ fontSize: 18, opacity: 0.8 }}>
        {isAll ? "\uD83D\uDCC1" : "\uD83D\uDCC2"}
      </span>

      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {isAll ? "All Brands" : (brand?.name || `Brand #${brand?.id}`)}
      </span>

      {/* LLM186: blue active-runs badge — separate from total-count
          so "in-flight right now" reads visually distinct from "ever
          had work". */}
      {activeCount > 0 && (
        <span
          style={{
            background: selected ? "rgba(191,219,254,0.95)" : "#eff6ff",
            color: "#1e3a8a",
            fontSize: 10,
            fontWeight: 800,
            borderRadius: 999,
            padding: "1px 7px",
            border: "1px solid #bfdbfe",
            lineHeight: 1.4,
          }}
        >
          ● {activeCount}
        </span>
      )}

      {count > 0 && (
        <span
          style={{
            background: selected ? GOLD : "#e8e8e8",
            color: selected ? NAVY : "#555",
            fontSize: 11,
            fontWeight: 700,
            borderRadius: 10,
            padding: "2px 8px",
            minWidth: 22,
            textAlign: "center",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function SessionCard({ session, brandName, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "#fff",
        borderRadius: 10,
        padding: "16px 20px",
        cursor: "pointer",
        border: "1px solid #e4e4e4",
        transition: "box-shadow 0.15s, border-color 0.15s",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.1)";
        e.currentTarget.style.borderColor = GOLD;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = "#e4e4e4";
      }}
    >
      {/* Title row */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 16, fontWeight: 600, color: NAVY, flex: 1 }}>
          {session.title || "Untitled Session"}
        </span>
        {session.is_destroyed && (
          <span
            style={{
              fontSize: 11,
              background: "#fee",
              color: "#c00",
              borderRadius: 4,
              padding: "1px 6px",
              fontWeight: 600,
            }}
          >
            Archived
          </span>
        )}
      </div>

      {/* Meta row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          flexWrap: "wrap",
          fontSize: 13,
          color: "#666",
        }}
      >
        {brandName && (
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontWeight: 600, color: NAVY }}>{brandName}</span>
          </span>
        )}

        <span>{fmtDate(session.created_at)}</span>
        <span style={{ opacity: 0.7 }}>{fmtTime(session.created_at)}</span>

        {session.default_model && (
          <span
            style={{
              background: "#f0f0f0",
              borderRadius: 4,
              padding: "1px 7px",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {modelLabel(session.default_model)}
          </span>
        )}
      </div>

      {/* Audit info */}
      {session.audit && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 12,
            color: "#888",
            marginTop: 4,
            paddingTop: 6,
            borderTop: "1px solid #f3f3f3",
          }}
        >
          <span style={{ fontWeight: 600, color: "#555" }}>Audit:</span>
          <span>{session.audit.title || `#${session.audit.id}`}</span>
          {session.audit.created_at && (
            <span style={{ opacity: 0.7 }}>{fmtDate(session.audit.created_at)}</span>
          )}
          {session.audit.models && session.audit.models.length > 0 && (
            <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {session.audit.models.map((m, i) => (
                <span
                  key={i}
                  style={{
                    background: "#e8e8ff",
                    color: "#4444aa",
                    borderRadius: 3,
                    padding: "0 5px",
                    fontSize: 11,
                    fontWeight: 500,
                  }}
                >
                  {m}
                </span>
              ))}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function EmptyState({ message }) {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "60px 20px",
        color: "#999",
      }}
    >
      <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.4 }}>
        {"\uD83D\uDCCB"}
      </div>
      <p style={{ fontSize: 16, margin: 0 }}>{message}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export default function AnalysisHistoryPage({ currentUser }) {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const brandFromUrl = searchParams.get("brand");

  // State
  const [brands, setBrands] = useState([]);
  const [sessionsByBrand, setSessionsByBrand] = useState({});
  const [selectedBrandId, setSelectedBrandId] = useState(brandFromUrl ? Number(brandFromUrl) : null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // LLM186: active-runs map for sidebar badges. Same endpoint as the
  // Brands Hub chip; refreshed every 30s.
  const [activeByBrand, setActiveByBrand] = useState({});

  // Analysis Runs (new primary view)
  const [viewMode, setViewMode] = useState("runs"); // "runs" | "sessions"
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all | in_progress | needs_review | ready | delivered | failed | pending
  const [stageFilter, setStageFilter] = useState("all"); // all | 1 | 2 | 3 | 4
  const [assigneeFilter, setAssigneeFilter] = useState("all"); // all | me
  const [abortTarget, setAbortTarget] = useState(null);
  const [aborting, setAborting] = useState(false);
  const [runsRefresh, setRunsRefresh] = useState(0);

  // ---- Fetch brands then sessions ----
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const brandData = await fetchBrands();
      // fetchBrands may return { brands: [...] } or an array directly
      const brandList = Array.isArray(brandData) ? brandData : brandData?.brands || [];
      setBrands(brandList);

      // Fetch sessions for each brand in parallel
      const results = await Promise.allSettled(
        brandList.map((b) =>
          copilotListSessions(b.id).then((res) => ({
            brandId: b.id,
            items: res?.items || [],
          }))
        )
      );

      const map = {};
      for (const r of results) {
        if (r.status === "fulfilled" && r.value.items.length > 0) {
          map[r.value.brandId] = r.value.items;
        }
      }
      setSessionsByBrand(map);
    } catch (err) {
      console.error("AnalysisHistoryPage load error:", err);
      setError(err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Fetch analysis_runs (all brands). Re-fetch when filters change or an
  // action (abort) pokes runsRefresh. Also polls every 15s while the tab
  // is visible AND there's at least one in-flight run, so status updates
  // (running_audit → audit_complete → analysis_complete → ready) appear
  // here without the user having to manually refresh.
  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;
    const IN_FLIGHT = ["running_audit", "running_analysis", "generating_pptx"];

    const fetchRuns = (showSpinner) => {
      if (showSpinner) setRunsLoading(true);
      return analysisRunsList({ brandId: selectedBrandId ?? null })
        .then((resp) => {
          if (cancelled) return [];
          const items = Array.isArray(resp?.items) ? resp.items : [];
          setRuns(items);
          return items;
        })
        .catch((e) => {
          if (!cancelled && showSpinner) {
            setError(e?.message || "Failed to load analysis runs");
          }
          return [];
        })
        .finally(() => {
          if (!cancelled && showSpinner) setRunsLoading(false);
        });
    };

    // After every fetch, decide whether to schedule another poll based on
    // the FRESH list — keeps the dep array clean (no `runs` dependency,
    // so the effect doesn't re-run on every state update).
    const tick = (showSpinner) => {
      fetchRuns(showSpinner).then((items) => {
        if (cancelled) return;
        const hasMoving = items.some((r) => IN_FLIGHT.includes(r.status));
        if (!hasMoving) return;
        pollTimer = setTimeout(() => {
          if (document.visibilityState === "visible") {
            tick(false);
          } else {
            // Tab is hidden; re-arm immediately so visibilitychange picks it up.
            tick(false);
          }
        }, 15_000);
      });
    };
    tick(true);

    const onVisible = () => {
      if (document.visibilityState === "visible") fetchRuns(false);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [selectedBrandId, runsRefresh]);

  // ---- Run actions ----
  const onResumeRun = useCallback(
    (run) => {
      // LLM180: terminal runs (delivered/failed) route to the canonical
      // detail page — there's nothing left to configure, you just want to
      // inspect deliverables. Non-terminal runs go straight back into the
      // wizard so the user can keep working.
      if (run.status === "delivered" || run.status === "failed") {
        navigate(`/analysis-runs/${run.id}`);
      } else {
        navigate(`/brands/${run.brand_id}/new-analysis?runId=${run.id}`);
      }
    },
    [navigate]
  );

  const onAbortRun = useCallback((run) => {
    setAbortTarget(run);
  }, []);

  const confirmAbort = useCallback(async () => {
    if (!abortTarget) return;
    setAborting(true);
    try {
      // LLM171: atomic abort also cancels in-flight SRA runs so workers
      // stop burning tokens within seconds.
      await analysisRunAbort(abortTarget.id);
      setRunsRefresh((v) => v + 1);
    } catch (e) {
      setError(e?.message || "Failed to abort run");
    } finally {
      setAborting(false);
      setAbortTarget(null);
    }
  }, [abortTarget]);

  const onDeleteRun = useCallback(async (run) => {
    if (!window.confirm(`Delete Run #${run.id}? It will be hidden from the hub; historical data is preserved.`)) {
      return;
    }
    try {
      await analysisRunSoftDelete(run.id);
      setRunsRefresh((v) => v + 1);
    } catch (e) {
      setError(e?.message || "Delete failed");
    }
  }, []);

  // Filtered runs (respects status + stage chips + search).
  const visibleRuns = useMemo(() => {
    let list = runs.slice();

    if (statusFilter !== "all") {
      const allowed = new Set(STATUS_GROUPS[statusFilter] || []);
      list = list.filter((r) => allowed.has(r.status));
    }

    if (stageFilter !== "all") {
      const wantStage = Number(stageFilter);
      list = list.filter((r) => stageForStatus(r.status) === wantStage);
    }

    if (assigneeFilter === "me" && currentUser?.id != null) {
      list = list.filter((r) => Number(r.assignee_user_id) === Number(currentUser.id));
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) => {
        const t = (r.title || "").toLowerCase();
        const brand = brands.find((b) => b.id === r.brand_id);
        const bn = (brand?.name || "").toLowerCase();
        return t.includes(q) || bn.includes(q) || String(r.id).includes(q);
      });
    }

    list.sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db_ = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db_ - da;
    });
    return list;
  }, [runs, statusFilter, stageFilter, assigneeFilter, currentUser, search, brands]);

  // Counts per filter group (for the chip badges).
  const statusCounts = useMemo(() => {
    const out = { all: runs.length };
    for (const key of Object.keys(STATUS_GROUPS)) {
      const set = new Set(STATUS_GROUPS[key]);
      out[key] = runs.filter((r) => set.has(r.status)).length;
    }
    return out;
  }, [runs]);

  // ---- Derived data ----

  // LLM186: poll active-runs map for the sidebar badges.
  useEffect(() => {
    let cancelled = false;
    const fetchActive = async () => {
      try {
        const res = await analysisRunsActiveByBrand();
        if (cancelled) return;
        const map = {};
        for (const row of res?.items || []) map[Number(row.brand_id)] = row;
        setActiveByBrand(map);
      } catch {
        // Silent — badges just won't update this tick
      }
    };
    fetchActive();
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchActive();
    };
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") fetchActive();
    }, 30_000);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, []);

  // All brands in the sidebar — brands with sessions sorted by activity
  // (session count desc), then empty brands alphabetically. Empty brands stay
  // visible so users can start a new analysis from here even before their
  // first audit.
  const brandsWithSessions = useMemo(() => {
    const withCount = brands.map((b) => ({
      ...b,
      _sessionCount: sessionsByBrand[b.id]?.length || 0,
    }));
    const active = withCount
      .filter((b) => b._sessionCount > 0)
      .sort((a, b) => b._sessionCount - a._sessionCount);
    const empty = withCount
      .filter((b) => b._sessionCount === 0)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return [...active, ...empty];
  }, [brands, sessionsByBrand]);

  // Total session count
  const totalCount = useMemo(() => {
    return Object.values(sessionsByBrand).reduce((s, arr) => s + arr.length, 0);
  }, [sessionsByBrand]);

  // Filtered + sorted sessions for the main area
  const visibleSessions = useMemo(() => {
    let sessions = [];

    if (selectedBrandId === null) {
      // All brands
      for (const [brandId, items] of Object.entries(sessionsByBrand)) {
        for (const s of items) {
          sessions.push({ ...s, _brandId: Number(brandId) });
        }
      }
    } else {
      const items = sessionsByBrand[selectedBrandId] || [];
      sessions = items.map((s) => ({ ...s, _brandId: selectedBrandId }));
    }

    // Filter by search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      sessions = sessions.filter((s) => {
        const title = (s.title || "").toLowerCase();
        const model = (s.default_model || "").toLowerCase();
        const brand = brands.find((b) => b.id === s._brandId);
        const brandName = (brand?.name || "").toLowerCase();
        return title.includes(q) || model.includes(q) || brandName.includes(q);
      });
    }

    // Sort newest first
    sessions.sort((a, b) => {
      const da = a.created_at ? new Date(a.created_at).getTime() : 0;
      const db_ = b.created_at ? new Date(b.created_at).getTime() : 0;
      return db_ - da;
    });

    return sessions;
  }, [sessionsByBrand, selectedBrandId, search, brands]);

  // Brand name lookup
  const brandNameMap = useMemo(() => {
    const m = {};
    for (const b of brands) m[b.id] = b.name || `Brand #${b.id}`;
    return m;
  }, [brands]);

  // ---- Responsive: detect narrow viewport ----
  const [narrow, setNarrow] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const showSidebar = narrow ? sidebarOpen : true;

  // ---- Render ----
  return (
    <AppShell
      headerSubtitle="Analysis History"
      headerRight={
        <UserActionsBar
          mode="module"
          currentUser={currentUser}
          onBackToModules={() => navigate("/modules")}
        />
      }
    >
      {/* Mobile toggle */}
      {narrow && (
        <button
          onClick={() => setSidebarOpen((v) => !v)}
          style={{
            marginBottom: 12,
            padding: "8px 16px",
            borderRadius: 8,
            border: `1px solid ${NAVY}`,
            background: sidebarOpen ? NAVY : "#fff",
            color: sidebarOpen ? "#fff" : NAVY,
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          {sidebarOpen ? "Hide Brands" : "Show Brands"} ({brandsWithSessions.length})
        </button>
      )}

      {error && (
        <div
          style={{
            background: "#fff0f0",
            color: "#c00",
            padding: "12px 16px",
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 14,
          }}
        >
          {error}
          <button
            onClick={loadData}
            style={{
              marginLeft: 12,
              padding: "4px 12px",
              borderRadius: 6,
              border: "1px solid #c00",
              background: "transparent",
              color: "#c00",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={{ display: "flex", gap: 20, minHeight: 400 }}>
        {/* ---- Sidebar ---- */}
        {showSidebar && (
          <div
            style={{
              width: narrow ? "100%" : SIDEBAR_W,
              minWidth: narrow ? undefined : SIDEBAR_W,
              flexShrink: 0,
              background: "#fafafa",
              borderRadius: 12,
              padding: 12,
              display: "flex",
              flexDirection: "column",
              gap: 4,
              overflowY: "auto",
              maxHeight: narrow ? 300 : "calc(100vh - 260px)",
              border: "1px solid #eee",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.6,
                color: "#888",
                padding: "6px 14px 8px",
              }}
            >
              Brands
            </div>

            <BrandFolder
              brand={null}
              count={totalCount}
              activeCount={Object.values(activeByBrand).reduce((sum, b) => sum + (b.count || 0), 0)}
              selected={selectedBrandId === null}
              onClick={() => {
                setSelectedBrandId(null);
                if (narrow) setSidebarOpen(false);
              }}
            />

            {brandsWithSessions.map((b) => {
              const ab = activeByBrand[Number(b.id)];
              return (
                <BrandFolder
                  key={b.id}
                  brand={b}
                  count={sessionsByBrand[b.id]?.length || 0}
                  activeCount={ab?.count || 0}
                  activeRuns={ab?.runs || []}
                  selected={selectedBrandId === b.id}
                  onClick={() => {
                    setSelectedBrandId(b.id);
                    if (narrow) setSidebarOpen(false);
                  }}
                />
              );
            })}

            {brandsWithSessions.length === 0 && !loading && (
              <div style={{ padding: "12px 14px", fontSize: 13, color: "#999" }}>
                No brands yet.{" "}
                <a href="/brands-hub" style={{ color: NAVY, fontWeight: 700 }}>
                  Create one
                </a>
                .
              </div>
            )}
          </div>
        )}

        {/* ---- Main area ---- */}
        {(!narrow || !sidebarOpen) && (
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Header row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: 20,
                  fontWeight: 700,
                  color: NAVY,
                  flex: 1,
                  minWidth: 140,
                }}
              >
                {selectedBrandId === null
                  ? "All Brands"
                  : brandNameMap[selectedBrandId] || "Brand"}
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    color: "#888",
                    marginLeft: 10,
                  }}
                >
                  ({viewMode === "runs" ? visibleRuns.length : visibleSessions.length}{" "}
                  {viewMode === "runs" ? "analyses" : "chats"})
                </span>
              </h2>

              {/* View-mode toggle */}
              <div
                style={{
                  display: "inline-flex",
                  background: "#f3f4f6",
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 2,
                }}
              >
                {[
                  { key: "runs", label: "Analyses" },
                  { key: "sessions", label: "Older chats" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setViewMode(opt.key)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 8,
                      border: "none",
                      background: viewMode === opt.key ? "#fff" : "transparent",
                      color: viewMode === opt.key ? NAVY : "#6b7280",
                      fontWeight: viewMode === opt.key ? 800 : 600,
                      fontSize: 12,
                      cursor: "pointer",
                      boxShadow: viewMode === opt.key ? "0 1px 3px rgba(15,23,42,0.08)" : "none",
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <button
                onClick={() => navigate("/templates")}
                title="Manage reusable question sets and model tiers"
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  color: NAVY,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Templates
              </button>

              <button
                onClick={() => {
                  if (selectedBrandId == null) return;
                  navigate(`/brands/${selectedBrandId}/new-analysis`);
                }}
                disabled={selectedBrandId == null}
                title={
                  selectedBrandId == null
                    ? "Pick a brand in the sidebar first"
                    : `Start a new analysis for ${brandNameMap[selectedBrandId]}`
                }
                style={{
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: `1px solid ${GOLD}`,
                  background: selectedBrandId == null ? "#f3f4f6" : NAVY,
                  color: selectedBrandId == null ? "#9ca3af" : GOLD,
                  fontWeight: 900,
                  fontSize: 13,
                  cursor: selectedBrandId == null ? "not-allowed" : "pointer",
                }}
              >
                + New Analysis
              </button>

              <div style={{ width: 260, flexShrink: 0 }}>
                <SearchBar
                  value={search}
                  onChange={setSearch}
                  placeholder="Filter by title, model, brand..."
                />
              </div>
            </div>

            {/* Loading */}
            {viewMode === "runs" ? (
              <RunsPane
                loading={runsLoading}
                runs={visibleRuns}
                brandNameMap={brandNameMap}
                showBrandName={selectedBrandId === null}
                statusFilter={statusFilter}
                setStatusFilter={setStatusFilter}
                stageFilter={stageFilter}
                setStageFilter={setStageFilter}
                assigneeFilter={assigneeFilter}
                setAssigneeFilter={setAssigneeFilter}
                statusCounts={statusCounts}
                onResume={onResumeRun}
                onAbort={onAbortRun}
                onDelete={onDeleteRun}
              />
            ) : (
              <>
                {loading && (
                  <div style={{ padding: 40, textAlign: "center", color: "#888" }}>
                    Loading sessions...
                  </div>
                )}

                {!loading && visibleSessions.length === 0 && (
                  <EmptyState
                    message={
                      search.trim()
                        ? "No sessions match your search."
                        : selectedBrandId !== null
                          ? "No analysis sessions for this brand yet."
                          : "No analysis sessions found. Start a new analysis from a brand."
                    }
                  />
                )}

                {!loading && visibleSessions.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {visibleSessions.map((s) => (
                      <SessionCard
                        key={s.id}
                        session={s}
                        brandName={
                          selectedBrandId === null
                            ? brandNameMap[s._brandId]
                            : null
                        }
                        onClick={() => navigate(`/analysis/${s.id}`)}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!abortTarget}
        title="Abort this analysis?"
        message={abortTarget ? <AbortDialogMessage run={abortTarget} /> : null}
        confirmText={aborting ? "Aborting…" : "Abort run"}
        cancelText="Cancel"
        onConfirm={confirmAbort}
        onCancel={() => setAbortTarget(null)}
      />
    </AppShell>
  );
}


// Context-aware copy for the Abort dialog: wording depends on whether the
// run is between stages (user is deliberating) or actively executing one.
function AbortDialogMessage({ run }) {
  const inFlight = ["running_audit", "running_analysis", "generating_pptx"].includes(
    run.status
  );
  const stage = stageForStatus(run.status);
  const stageLabel = WIZARD_STAGES.find((s) => s.key === stage)?.label || "";

  const context = (() => {
    switch (run.status) {
      case "pending":
        return "The run hasn't started yet. Aborting removes it as an active analysis.";
      case "running_audit":
        return "The audit is executing on the backend. Aborting stops wizard progression, but in-flight model runs continue until the worker completes them.";
      case "audit_complete":
        return "The audit finished; the run is waiting for your approval to generate analysis. Aborting stops it here \u2014 the audit matrix is preserved.";
      case "running_analysis":
        return "Your brand analysis is being generated. Aborting stops the wizard from moving on; any analysis already being written finishes in the background.";
      case "analysis_complete":
        return "Analysis is complete; the run is waiting for your approval to generate PPTX. Aborting stops it here \u2014 audit + analysis are preserved.";
      case "generating_pptx":
        return "PPTX generation is running in the background. Aborting stops wizard progression; the background task will continue unless it fails on its own.";
      case "ready":
        return "PPTX is ready and waiting for you to mark it delivered. Aborting marks the run as failed instead \u2014 the PPTX artifact is still downloadable.";
      default:
        return "The run will stop advancing. Historical data is preserved.";
    }
  })();

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        Run <strong>#{run.id}</strong>
        {run.title ? ` "${run.title}"` : ""} (currently at{" "}
        <strong>
          Stage {stage}: {stageLabel}
        </strong>
        ) will be marked <strong>failed</strong>.
      </div>
      <div style={{ fontSize: 12, color: "#6b7280" }}>{context}</div>
      {inFlight && (
        <div style={{ fontSize: 11, color: "#92400e", marginTop: 8, fontStyle: "italic" }}>
          In-flight model runs for this audit will also be cancelled
          within a few seconds so no extra tokens are burned.
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------
// Analysis Runs pane
// -----------------------------------------------------------

function RunsPane({
  loading,
  runs,
  brandNameMap,
  showBrandName,
  statusFilter,
  setStatusFilter,
  stageFilter,
  setStageFilter,
  assigneeFilter,
  setAssigneeFilter,
  statusCounts,
  onResume,
  onAbort,
  onDelete,
}) {
  const STATUS_CHIPS = [
    { key: "all", label: "All" },
    { key: "in_progress", label: "In progress" },
    { key: "needs_review", label: "Needs review" },
    { key: "ready", label: "Ready" },
    { key: "delivered", label: "Delivered" },
    { key: "failed", label: "Failed" },
    { key: "pending", label: "Pending" },
  ];

  return (
    <>
      {/* Filter bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {STATUS_CHIPS.map((c) => {
            const active = statusFilter === c.key;
            const count = statusCounts?.[c.key] ?? 0;
            return (
              <button
                key={c.key}
                onClick={() => setStatusFilter(c.key)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  border: active ? "1px solid #111827" : "1px solid #e5e7eb",
                  background: active ? "#111827" : "#fff",
                  color: active ? "#ffcc33" : "#374151",
                  fontSize: 12,
                  fontWeight: active ? 800 : 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {c.label}
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    background: active ? "rgba(255,204,51,0.15)" : "#f3f4f6",
                    padding: "1px 6px",
                    borderRadius: 999,
                    color: active ? "#ffcc33" : "#6b7280",
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ width: 1, height: 24, background: "#e5e7eb" }} />

        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Stage:
          </span>
          {[
            { k: "all", l: "All" },
            { k: "1", l: "1 Configure" },
            { k: "2", l: "2 Audit" },
            { k: "3", l: "3 Analysis" },
            { k: "4", l: "4 Deliver" },
          ].map((s) => {
            const active = stageFilter === s.k;
            return (
              <button
                key={s.k}
                onClick={() => setStageFilter(s.k)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 8,
                  border: active ? "1px solid #d4a017" : "1px solid #e5e7eb",
                  background: active ? "#fffbe8" : "#fff",
                  color: "#374151",
                  fontSize: 11,
                  fontWeight: active ? 800 : 600,
                  cursor: "pointer",
                }}
              >
                {s.l}
              </button>
            );
          })}
        </div>

        <div style={{ width: 1, height: 24, background: "#e5e7eb" }} />

        {/* LLM198: assignee filter (All / Assigned to me) */}
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Owner:
          </span>
          {[
            { k: "all", l: "All" },
            { k: "me", l: "Assigned to me" },
          ].map((a) => {
            const active = (assigneeFilter || "all") === a.k;
            return (
              <button
                key={a.k}
                onClick={() => setAssigneeFilter(a.k)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 8,
                  border: active ? "1px solid #2563eb" : "1px solid #e5e7eb",
                  background: active ? "#eff6ff" : "#fff",
                  color: active ? "#1e3a8a" : "#374151",
                  fontSize: 11,
                  fontWeight: active ? 800 : 600,
                  cursor: "pointer",
                }}
              >
                {a.l}
              </button>
            );
          })}
        </div>
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: "center", color: "#888" }}>
          Loading analyses…
        </div>
      )}

      {!loading && runs.length === 0 && (
        <EmptyState
          message={
            statusFilter !== "all" || stageFilter !== "all"
              ? "No runs match the current filters."
              : "No analyses yet. Start one from a brand."
          }
        />
      )}

      {!loading && runs.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {runs.map((r) => (
            <RunCard
              key={r.id}
              run={r}
              brandName={showBrandName ? brandNameMap[r.brand_id] : null}
              onResume={() => onResume(r)}
              onAbort={() => onAbort(r)}
              onDelete={() => onDelete(r)}
            />
          ))}
        </div>
      )}
    </>
  );
}


function RunCard({ run, brandName, onResume, onAbort, onDelete }) {
  const stage = stageForStatus(run.status);
  const color = statusColor(run.status);
  const terminal = run.status === "delivered" || run.status === "failed";
  // Abort is allowed in every non-terminal state \u2014 between stages (pending /
  // audit_complete / analysis_complete / ready) and during stages (running_*).
  const canAbort = !terminal;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 14,
        display: "grid",
        gridTemplateColumns: "1fr auto",
        gap: 12,
        alignItems: "center",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: NAVY }}>
            {run.title || `Run #${run.id}`}
          </div>
          {brandName && (
            <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280" }}>
              · {brandName}
            </span>
          )}
          <span
            style={{
              padding: "2px 10px",
              borderRadius: 999,
              background: color.bg,
              color: color.fg,
              fontSize: 10,
              fontWeight: 900,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {run.status.replace(/_/g, " ")}
          </span>
          {run.validator_status && (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background:
                  run.validator_status === "green"
                    ? "#10b981"
                    : run.validator_status === "yellow"
                      ? "#d4a017"
                      : "#dc2626",
              }}
              title={`Quality: ${run.validator_status === "green" ? "Passed" : run.validator_status === "yellow" ? "Warnings" : "Issues"}`}
            />
          )}
          {run.auto_advance && (
            <span
              style={{
                padding: "2px 8px",
                borderRadius: 999,
                background: "#eff6ff",
                color: "#1e3a8a",
                fontSize: 10,
                fontWeight: 800,
                border: "1px solid #bfdbfe",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
              title="This run is running end-to-end; you'll be notified when it finishes."
            >
              ⚡ Auto
            </span>
          )}
          {/* LLM184: revision count chip */}
          {run.revision_count > 0 && (
            <span
              style={{
                padding: "2px 8px",
                borderRadius: 999,
                background: "#fef3c7",
                color: "#92400e",
                fontSize: 10,
                fontWeight: 800,
                border: "1px solid #fde68a",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
              title={`This analysis has been revised ${run.revision_count} time${run.revision_count === 1 ? "" : "s"}.`}
            >
              ↻ v{run.revision_count + 1}
            </span>
          )}
        </div>

        {/* Stage breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}>
          {WIZARD_STAGES.map((s, i) => {
            const done = i < stage - 1;
            const active = i === stage - 1;
            return (
              <React.Fragment key={s.key}>
                <div
                  style={{
                    padding: "2px 8px",
                    borderRadius: 6,
                    fontSize: 10,
                    fontWeight: active ? 900 : 700,
                    background: active ? NAVY : done ? "#e5e7eb" : "#f9fafb",
                    color: active ? GOLD : done ? "#374151" : "#9ca3af",
                    border: active ? `1px solid ${GOLD}` : "1px solid #e5e7eb",
                  }}
                >
                  {s.key}. {s.label}
                </div>
                {i < WIZARD_STAGES.length - 1 && (
                  <span style={{ color: done ? GOLD : "#d1d5db", fontSize: 10 }}>›</span>
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div style={{ fontSize: 11, color: "#6b7280" }}>
          Created {fmtDate(run.created_at)} · {fmtTime(run.created_at)}
          {run.actual_cost_usd != null && (
            <>
              {" · "}
              <CostBreakdown runId={run.id} totalUsd={Number(run.actual_cost_usd)} />
            </>
          )}
          {run.delivered_at && <> · Delivered {fmtDate(run.delivered_at)}</>}
          {run.assignee && (
            <> · Assigned to <strong style={{ color: "#1e3a8a" }}>{run.assignee.full_name || run.assignee.email}</strong></>
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 6 }}>
        <button
          onClick={onResume}
          title={terminal ? "Open run" : "Resume at current stage"}
          style={{
            padding: "8px 14px",
            borderRadius: 10,
            border: `1px solid ${GOLD}`,
            background: NAVY,
            color: GOLD,
            fontWeight: 800,
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          {terminal ? "Open" : "Resume →"}
        </button>
        {canAbort && (
          <button
            onClick={onAbort}
            title="Mark this run as failed; stops wizard progression"
            style={{
              padding: "8px 12px",
              borderRadius: 10,
              border: "1px solid #fecaca",
              background: "#fff",
              color: "#b91c1c",
              fontWeight: 700,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Abort
          </button>
        )}
        <button
          onClick={onDelete}
          title="Soft-delete run (hidden from hub; data preserved)"
          style={{
            padding: "8px 10px",
            borderRadius: 10,
            border: "1px solid #e5e7eb",
            background: "#fff",
            color: "#6b7280",
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
